import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Card, CardContent, Alert, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { 
  getUserMovieInteraction,
  markAsWatched,
  unmarkAsWatched
} from '../../services/movieInteractionApi';
import { generateClient } from 'aws-amplify/api';
import { getUserWatchedQuery } from '../../graphql/watchlist';

const WatchedToggleTest = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [interaction, setInteraction] = useState(null);
  const [watchedItems, setWatchedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState([]);

  // Test movie data
  const testMovie = {
    id: '507f1f77bcf86cd799439012', // Different test movie ID
    name: 'Watched Test Movie',
    title: 'Watched Test Movie'
  };

  const addResult = (test, success, message) => {
    setTestResults(prev => [...prev, { test, success, message, timestamp: new Date() }]);
  };

  const loadData = async () => {
    if (!user?.id) return;
    try {
      // Load interaction
      const interactionResult = await getUserMovieInteraction(user.id, testMovie.id);
      setInteraction(interactionResult);
      
      // Load watched items from legacy table
      const client = generateClient();
      const watchedResult = await client.graphql({
        query: getUserWatchedQuery,
        variables: { userID: user.id }
      });
      
      const userWatchedItems = watchedResult.data?.listWatchedItems?.items?.filter(
        item => item.movieID === testMovie.id
      ) || [];
      
      setWatchedItems(userWatchedItems);
      
      addResult('Load Data', true, `Interaction: ${JSON.stringify(interactionResult)}, WatchedItems: ${userWatchedItems.length}`);
    } catch (error) {
      addResult('Load Data', false, error.message);
    }
  };

  const testMarkAsWatched = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const result = await markAsWatched(user.id, testMovie.id);
      await loadData(); // Refresh data
      addResult('Mark as Watched', true, 'Successfully marked as watched');
      addNotification({ type: 'success', message: 'Marked as watched' });
    } catch (error) {
      addResult('Mark as Watched', false, error.message);
      addNotification({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const testUnmarkAsWatched = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      // Record before state
      const beforeWatchedItems = watchedItems.length;
      
      const result = await unmarkAsWatched(user.id, testMovie.id);
      
      // Wait a moment for sync to complete
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      await loadData(); // Refresh data
      
      // Check if legacy table was properly cleaned
      const afterWatchedItems = watchedItems.length;
      const deletionSuccess = afterWatchedItems < beforeWatchedItems;
      
      addResult('Unmark as Watched', true, 
        `Successfully unmarked as watched. Legacy table entries: ${beforeWatchedItems} → ${afterWatchedItems} (${deletionSuccess ? 'DELETED' : 'NOT DELETED'})`);
      addNotification({ type: 'success', message: 'Unmarked as watched' });
      
      if (!deletionSuccess && beforeWatchedItems > 0) {
        addResult('Legacy Table Cleanup', false, 'WatchedItem entries were not properly deleted!');
      } else if (deletionSuccess) {
        addResult('Legacy Table Cleanup', true, 'WatchedItem entries properly deleted');
      }
    } catch (error) {
      addResult('Unmark as Watched', false, error.message);
      addNotification({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setTestResults([]);
  };

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id]);

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please sign in to test watched functionality</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Watched Toggle & Legacy Table Cleanup Test
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        This test verifies that when a movie is unmarked as watched, it is properly removed from both the UserMovieInteraction table and the legacy WatchedItem table.
      </Alert>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current State
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            User ID: {user.id}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Test Movie ID: {testMovie.id}
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">UserMovieInteraction:</Typography>
            {interaction ? (
              <Box>
                <Typography variant="body2">
                  • Watched: {interaction.watched ? '✅ Yes' : '❌ No'}
                </Typography>
                <Typography variant="body2">
                  • In Watchlist: {interaction.inWatchlist ? '✅ Yes' : '❌ No'}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No interaction found
              </Typography>
            )}
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">WatchedItem Table (Legacy):</Typography>
            <Typography variant="body2" color={watchedItems.length > 0 ? 'success.main' : 'text.secondary'}>
              • Count: {watchedItems.length} entries {watchedItems.length > 0 ? '✅' : '❌'}
            </Typography>
            {watchedItems.map((item, index) => (
              <Typography key={index} variant="body2" color="text.secondary">
                • ID: {item.id}, Status: {item.status}, Created: {new Date(item.createdAt).toLocaleString()}
              </Typography>
            ))}
            {watchedItems.length === 0 && (
              <Typography variant="body2" color="success.main">
                ✅ No entries found - properly cleaned up!
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button 
          variant="outlined" 
          onClick={loadData}
          disabled={loading}
        >
          Refresh Data
        </Button>
        
        <Button 
          variant="contained" 
          color="success"
          onClick={testMarkAsWatched}
          disabled={loading || interaction?.watched}
        >
          Mark as Watched
        </Button>
        
        <Button 
          variant="contained" 
          color="error"
          onClick={testUnmarkAsWatched}
          disabled={loading || !interaction?.watched}
        >
          Unmark as Watched & Test Cleanup
        </Button>
        
        <Button 
          variant="outlined" 
          color="secondary"
          onClick={clearResults}
        >
          Clear Results
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress />
        </Box>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Test Results
          </Typography>
          
          {testResults.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No tests run yet
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {testResults.map((result, index) => (
                <Alert 
                  key={index}
                  severity={result.success ? 'success' : 'error'}
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2">
                    {result.test} - {result.timestamp.toLocaleTimeString()}
                  </Typography>
                  <Typography variant="body2">
                    {result.message}
                  </Typography>
                </Alert>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default WatchedToggleTest;